import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "@/hooks/useUser";
import { CheckoutSignInOptions } from "./CheckoutSignInOptions";
import { rememberCheckoutFlowForPostAuthClaim } from "@/lib/checkout-state";
import { guestCheckoutRequest, saveGuestCheckoutDraft, type GuestCheckoutInfo } from "@/lib/guest-checkout-api";
import { readCheckoutCustomerDraft, saveCheckoutCustomerDraft, clearCheckoutCustomerDraft, emptyCustomerDraft } from "@/lib/checkout-customer-draft";
import { track } from "@/lib/analytics";
import type { GuestRecoverySelection } from "@workspace/api-client-react";

const fieldClass = "mt-1.5 block min-h-12 w-full rounded-xl border border-border bg-background px-3 py-3 text-base outline-none focus:border-foreground focus:ring-1 focus:ring-foreground";

export function GuestCheckoutDetails({ flowId, carId, recovery, draft, onSaved, onEditingChange, submitting }: {
  flowId: string; carId: number; recovery?: GuestRecoverySelection; draft?: GuestCheckoutInfo | null;
  onSaved?: (draft: GuestCheckoutInfo) => void; onEditingChange?: (editing: boolean) => void; submitting?: boolean;
}) {
  const auth = useUser();
  const { t } = useTranslation();
  const id = useId();
  const initial = readCheckoutCustomerDraft(flowId) ?? emptyCustomerDraft();
  const [firstName, setFirstName] = useState(initial.firstName);
  const [lastName, setLastName] = useState(initial.lastName);
  const [email, setEmail] = useState(initial.email);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const lock = useRef(false);
  const form = useRef<HTMLFormElement>(null);
  const isGuest = auth.user?.app_metadata?.checkout_guest === true;
  const authenticatedReady = isGuest && auth.profile?.is_complete === true && !auth.isLoading && !auth.profileError;
  const ready = authenticatedReady || !!draft && draft.flowId === flowId && !draft.claimed;
  const confirmed = (auth.profile as typeof auth.profile & { guest_email_confirmed?: boolean })?.guest_email_confirmed;

  useEffect(() => {
    if (ready && !editing) {
      setFirstName(authenticatedReady ? auth.profile?.first_name ?? "" : draft?.firstName ?? "");
      setLastName(authenticatedReady ? auth.profile?.last_name ?? "" : draft?.lastName ?? "");
      setEmail(authenticatedReady ? auth.profile?.email ?? "" : draft?.email ?? "");
    }
  }, [ready, authenticatedReady, editing, draft, auth.profile?.first_name, auth.profile?.last_name, auth.profile?.email]);
  useEffect(() => { onEditingChange?.(editing); }, [editing, onEditingChange]);
  useEffect(() => {
    if (!auth.user) saveCheckoutCustomerDraft(flowId, { ...emptyCustomerDraft(), firstName, lastName, email });
  }, [auth.user, flowId, firstName, lastName, email]);

  const continueToPayment = async () => {
    if (lock.current || !form.current?.reportValidity()) return;
    if (!firstName.trim() || !lastName.trim()) {
      setError(t("checkout.identity.details_required", { defaultValue: "Enter your first and last name." }));
      return;
    }
    lock.current = true; setBusy(true); setError("");
    const fields = { flowId, firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim().toLowerCase(),
      ...(recovery ? { recovery } : {}) };
    try {
      if (authenticatedReady) {
        const guest = await guestCheckoutRequest<{ guest: { flowId: string } | null }>("", { token: auth.session?.access_token });
        await guestCheckoutRequest("", {
          method: "PATCH", token: auth.session?.access_token,
          body: { ...fields, flowId: guest.guest?.flowId ?? flowId },
        });
        auth.retryProfile();
        setEditing(false);
      } else {
        rememberCheckoutFlowForPostAuthClaim(flowId);
        if (!recovery) throw new Error(t("checkout.guest.details_unavailable", { defaultValue: "We couldn't load your details. Try again to continue." }));
        const saved = await saveGuestCheckoutDraft({ ...fields, recovery });
        onSaved?.(saved);
        setEditing(false);
        clearCheckoutCustomerDraft(flowId);
        track("checkout_guest_details_saved", { checkout_journey_id: flowId, car_id: carId });
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("common.error"));
    } finally { lock.current = false; setBusy(false); }
  };

  if (isGuest && !ready) return <section id="checkout-customer-details" aria-labelledby={`${id}-heading`}
    className="min-w-0 rounded-2xl border border-border bg-background p-5 sm:p-6">
    <h2 id={`${id}-heading`} className="text-lg font-semibold">{t("checkout.identity.heading", { defaultValue: "Your details" })}</h2>
    {auth.profileError ? <div role="alert" className="mt-3 text-sm">
      <p>{t("checkout.guest.details_unavailable", { defaultValue: "We couldn't load your details. Try again to continue." })}</p>
      <button type="button" className="mt-3 min-h-11 underline" onClick={() => auth.retryProfile()}>
        {t("common.retry", { defaultValue: "Try again" })}
      </button>
    </div> : <p role="status" className="mt-3 text-sm text-muted-foreground">
      {t("checkout.guest.loading_details", { defaultValue: "Getting your details ready…" })}
    </p>}
    <CheckoutSignInOptions flowId={flowId} email={email} disabled={auth.isLoading} />
  </section>;

  return <section id="checkout-customer-details" data-testid="checkout-customer-details" aria-labelledby={`${id}-heading`} className="min-w-0 rounded-2xl border border-border bg-background p-5 sm:p-6">
    <div className="flex items-center justify-between gap-3">
      <h2 id={`${id}-heading`} className="text-lg font-semibold">{t("checkout.identity.heading", { defaultValue: "Your details" })}</h2>
      {ready && !editing && !confirmed && <button type="button" disabled={submitting} onClick={() => setEditing(true)}
        className="min-h-11 px-2 text-sm font-semibold underline underline-offset-4">{t("common.edit", { defaultValue: "Edit" })}</button>}
    </div>
    {ready && !editing ? <>
      <p className="mt-3 font-medium">{firstName} {lastName}</p>
      <p className="break-all text-sm text-muted-foreground">{email}</p>
      <p className="mt-3 text-sm text-muted-foreground">
        {t("checkout.guest.details_saved", { defaultValue: "Your details are saved. You can confirm your email after booking." })}
      </p>
      {isGuest && auth.profileError && <button type="button" className="mt-3 min-h-11 underline"
        onClick={() => auth.retryProfile()}>{t("common.retry", { defaultValue: "Try again" })}</button>}
    </> : <form ref={form} className="mt-4 space-y-4" onSubmit={(event) => { event.preventDefault(); void continueToPayment(); }}>
      <p className="text-sm text-muted-foreground">{t("checkout.guest.details_intro", { defaultValue: "Enter your details to continue as a guest." })}</p>
      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
        <label htmlFor={`${id}-first`} className="block text-sm font-medium">{t("auth.first_name")}
          <input id={`${id}-first`} name="given-name" autoComplete="given-name" required maxLength={100}
            value={firstName} onChange={(event) => setFirstName(event.target.value)} disabled={busy} className={fieldClass} />
        </label>
        <label htmlFor={`${id}-last`} className="block text-sm font-medium">{t("auth.last_name")}
          <input id={`${id}-last`} name="family-name" autoComplete="family-name" required maxLength={100}
            value={lastName} onChange={(event) => setLastName(event.target.value)} disabled={busy} className={fieldClass} />
        </label>
      </div>
      <label htmlFor={`${id}-email`} className="block text-sm font-medium">{t("auth.email")}
        <input id={`${id}-email`} name="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none"
          pattern="[^@\s]+@[^@\s]+\.[^@\s.]+" title={t("checkout.guest.email_format", { defaultValue: "Enter a valid email address, such as name@example.com." })}
          required maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy}
          aria-describedby={`${id}-hint`} className={fieldClass} />
      </label>
      <p id={`${id}-hint`} className="text-sm text-muted-foreground">
        {t("checkout.guest.email_hint", { defaultValue: "We'll send your booking details here. No password needed." })}
      </p>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <button type="submit" disabled={busy || auth.isLoading}
        className="min-h-12 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-primary-foreground disabled:opacity-60">
        {busy ? t("common.loading", { defaultValue: "One moment…" }) : ready
          ? t("common.save", { defaultValue: "Save" })
          : t("checkout.continue_to_payment", { defaultValue: "Continue to payment" })}
      </button>
      {ready && <button type="button" disabled={busy} onClick={() => setEditing(false)}
        className="min-h-11 w-full text-sm underline">{t("common.cancel")}</button>}
    </form>}
    <CheckoutSignInOptions flowId={flowId} email={email} disabled={busy || !!submitting} />
  </section>;
}
